import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileTypeFilter from '../../components/FileTypeFilter';

function AudioReplyInfo({ url, isDark = false }) {
  const [durationText, setDurationText] = React.useState('--:--');

  React.useEffect(() => {
    if (!url) {
      setDurationText('--:--');
      return undefined;
    }
    const audio = new Audio(url);
    audio.preload = 'metadata';

    const setFromSeconds = (secondsValue) => {
      const seconds = Math.max(0, Math.floor(secondsValue || 0));
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      setDurationText(`${mm}:${ss}`);
    };

    const trySetDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setFromSeconds(audio.duration);
        return;
      }

      const originalTime = audio.currentTime || 0;
      const restore = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setFromSeconds(audio.duration);
        }
        audio.currentTime = originalTime;
        audio.removeEventListener('timeupdate', restore);
      };
      audio.currentTime = 1e101;
      audio.addEventListener('timeupdate', restore);
    };

    audio.addEventListener('loadedmetadata', trySetDuration);
    audio.addEventListener('durationchange', trySetDuration);
    audio.load();
    return () => {
      audio.removeEventListener('loadedmetadata', trySetDuration);
      audio.removeEventListener('durationchange', trySetDuration);
      audio.src = '';
    };
  }, [url]);

  return (
    <Typography
      variant="caption"
      sx={{ color: isDark ? 'rgba(255,255,255,0.8)' : 'text.secondary', display: 'block', mt: 0.15 }}
    >
      {durationText}
    </Typography>
  );
}

function formatMessageTime(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const hhmmss = raw.match(/^(\d{2}):(\d{2})(:\d{2})?$/);
  if (hhmmss) return `${hhmmss[1]}:${hhmmss[2]}`;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) {
    const hh = String(iso.getHours()).padStart(2, '0');
    const mm = String(iso.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return raw.slice(0, 5);
}

function getReplyPreview(replyTo) {
  if (!replyTo) return '';
  if (replyTo.msgType === 'file') {
    const rawType =
      (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  }
  return replyTo.msgText || 'Mensagem';
}

function renderReplyPreview(replyTo, isDark = false) {
  if (!replyTo) return null;
  const textColor = isDark ? '#fff' : 'text.primary';
  if (replyTo.msgType !== 'file') {
    return <Typography variant="body2" sx={{ color: textColor }}>{replyTo.msgText || 'Mensagem'}</Typography>;
  }

  const fileType =
    (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
  const url = replyTo.urlFile || '';
  const label = getReplyPreview(replyTo);
  const commonThumbSx = {
    width: 44,
    height: 44,
    borderRadius: 1,
    objectFit: 'cover',
    border: isDark ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
    bgcolor: 'background.paper',
  };

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="video" src={url} muted sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEqIcon fontSize="small" />
        <Box>
          <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
          <AudioReplyInfo url={url} isDark={isDark} />
        </Box>
      </Box>
    );
  }
  if (fileType === 'pdf') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <InsertDriveFileIcon fontSize="small" />
      <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
    </Box>
  );
}

const MensagemCliente = React.memo(({ message, mediaMessagesRef, onReply }) => {
  const theme = useTheme();
  const fileType = `${message?.urlFile || ''}`.split('.').pop()?.toLowerCase();
  const isAudioMessage =
    message?.msgType === 'file' && ['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType);

  if (!mediaMessagesRef.current.has(message.id) && message.msgType === 'file') {
    mediaMessagesRef.current.set(
      message.id,
      <FileTypeFilter
        key={message.id}
        urlFile={message.urlFile}
        tone="outgoing"
        onReply={isAudioMessage ? () => onReply?.(message) : null}
      />
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          marginBottom: 1,
        }}
      >
        <Avatar
          alt="Nome Avatar"
          sx={{
            marginLeft: 2,
            width: 35,
            height: 35,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        />
        <Stack>
          <Box
            sx={{
              marginLeft: { xs: 0, sm: 10 },
              backgroundColor: isAudioMessage ? 'transparent' : theme.palette.primary.main,
              padding: isAudioMessage ? 0 : 1,
              borderRadius: isAudioMessage ? 0 : 1,
            }}
          >
            {message.replyTo && (
              <Box
                sx={{
                  mb: 1,
                  p: 0.8,
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderLeft: '3px solid rgba(255,255,255,0.7)',
                  bgcolor: 'rgba(0,0,0,0.18)',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Respondendo
                </Typography>
                {renderReplyPreview(message.replyTo, true)}
              </Box>
            )}

            {message.msgType === 'txt' && (
              <Typography sx={{ color: 'white' }}>{message.msgText}</Typography>
            )}

            {message.msgType === 'file' && mediaMessagesRef.current.get(message.id)}

            {!isAudioMessage && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  mt: 0.6,
                  display: 'block',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
                onClick={() => onReply?.(message)}
              >
                Responder
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(150, 149, 149, 0.94)',
              textAlign: 'right',
              alignSelf: 'flex-end',
              paddingTop: 0.5,
            }}
          >
            {formatMessageTime(message.date)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
});

export default MensagemCliente;


